<script>
	import { fly, scale } from 'svelte/transition';
	import { closeModal } from '$lib/modal-store';
	import { toastDisplay } from '$lib/utils.svelte';

	/**
	 * @typedef {import('$lib/types/contact.js').ContactFormData} ContactFormData
	 */

	/** @type {string} */
	let currentPage = $state('contact');
	
	/** @type {boolean} */
	let isSubmitting = $state(false);
	
	/** @type {ContactFormData} */
	let formData = $state({
		// Page 1: Contact
		fullName: '',
		email: '',
		company: '',
		position: '',
		// Page 2: Context
		sector: '',
		teamSize: '',
		timeline: '',
		rgpdConsent: false
	});

	/** @type {string[]} */
	const sectors = [
        'Cabinet d\'avocats',
        'Étude notariale',
		'Administration publique',
		'Banque et Finance',
		'Commerce et Distribution',
		'Assurance',
		'Immobilier',
		'Santé et Pharmaceutique',
		'Technologie et Numérique',
		'Industrie et Manufacturing',
		'Énergie et Utilities',
		'Autre'
	];

	/** @type {Array<{id: string, label: string, description: string}>} */
	const teamSizes = [
		{ id: 'solo', label: 'Solo', description: 'Travaille seul' },
		{ id: '2-5', label: '2-5', description: 'Petite équipe' },
		{ id: '6-20', label: '6-20', description: 'Équipe moyenne' },
		{ id: '20+', label: '20+', description: 'Grande équipe' }
	];

	/** @type {Array<{id: string, label: string, description: string, icon: string}>} */
	const timelines = [
		{ id: 'urgent', label: 'Urgent', description: '< 1 mois', icon: '⚡' },
		{ id: '3-6-mois', label: '3-6 mois', description: 'Planification courte', icon: '📅' },
		{ id: 'exploration', label: 'Exploration', description: '> 6 mois', icon: '🔍' }
	];

	/**
	 * @param {string} email
	 * @returns {boolean}
	 */
	function isValidEmail(email) {
		const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
		return emailRegex.test(email);
	}

	// Individual field validation states
	let fieldErrors = $derived({
		fullName: formData.fullName.length > 0 && formData.fullName.trim().length === 0,
		email: formData.email.length > 0 && !isValidEmail(formData.email),
		company: formData.company.length > 0 && formData.company.trim().length === 0,
		position: formData.position.length > 0 && formData.position.trim().length === 0
	});

	let isFormValid = $derived(
		currentPage === 'contact'
			? Boolean(formData.fullName && formData.email && isValidEmail(formData.email) && formData.company && formData.position)
			: Boolean(formData.sector && formData.teamSize && formData.timeline && formData.rgpdConsent)
	);

	function nextPage() {
		if (currentPage === 'contact' && isFormValid) {
			currentPage = 'context';
		}
	}

	function previousPage() {
		if (currentPage === 'context') {
			currentPage = 'contact';
		}
	}

	function submitForm() {
		if (isFormValid) {
			sendContactForm();
		}
	}

	/**
	 * Send contact form data to Notion via API
	 */
	async function sendContactForm() {
		isSubmitting = true;
		try {
			const response = await fetch('/api/contact', {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json'
				},
				body: JSON.stringify(formData)
			});

			if (response.ok) {
				const result = await response.json();
				closeModal();
				toastDisplay('Votre formulaire a été envoyé avec succès', 'success');
			} else {
				console.error('Form submission failed:', response.statusText);
				toastDisplay('Une erreur est survenue lors de l\'envoi du formulaire', 'error');
			}
		} catch (error) {
			console.error('Error submitting form:', error);
			toastDisplay('Une erreur est survenue lors de l\'envoi du formulaire', 'error');
		} finally {
			isSubmitting = false;
		}
	}

	/**
	 * @param {KeyboardEvent} event
	 */
	function handleKeyDown(event) {
		if (event.key === 'Escape') {
			closeModal();
		}
	}

	$effect(() => {
		window.addEventListener('keydown', handleKeyDown);
		return () => window.removeEventListener('keydown', handleKeyDown);
	});
</script>

<div
	role="dialog"
	class="modal z-30"
	in:fly|global={{ y: 50, duration: 300 }}
	out:fly|global={{ y: 50, duration: 250 }}
>
	<div
		class="pointer-events-auto mx-4 w-full max-w-xl bg-white dark:bg-ord-cardcolor-dark rounded-2xl overflow-hidden shadow-xl border border-gray-100 dark:border-ord-border-dark flex flex-col max-h-[85vh]"
		in:scale|global={{ start: 0.95, duration: 300, delay: 100 }}
		out:scale|global={{ start: 0.95, duration: 200 }}
	>
		<!-- Header -->
		<div class="px-6 py-6 border-b border-gray-100 dark:border-ord-border-dark">
			<div class="flex items-center justify-between mb-4">
				<div>
					<h2 class="text-2xl font-semibold text-gray-900 dark:text-white">Contactez-nous</h2>
					<p class="text-sm text-gray-500 dark:text-gray-400 mt-1 font-medium">
						{currentPage === 'contact' ? 'Vos informations' : 'Contexte du projet'}
					</p>
				</div>
				<button
					onclick={() => closeModal()}
					aria-label="Fermer le modal"
					class="text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 transition-all duration-200 p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 hover:scale-110"
				>
					<svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
						<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
					</svg>
				</button>
			</div>
			
			<!-- Progress indicator -->
			<div class="flex space-x-2">
				<div class="flex-1 h-1.5 bg-royal-blue rounded-full"></div>
				<div class="flex-1 h-1.5 {currentPage === 'context' ? 'bg-royal-blue' : 'bg-gray-200 dark:bg-gray-700'} rounded-full transition-all duration-500 ease-out"></div>
			</div>
		</div>

		<!-- Form Content -->
		<div class="p-6 flex-1 overflow-y-auto">
			{#if currentPage === 'contact'}
				<div class="space-y-5" in:fly|global={{ x: -20, duration: 300, delay: 100 }} out:fly|global={{ x: 20, duration: 200 }}>
					<div in:fly={{ y: 20, duration: 400, delay: 200 }}>
						<label for="fullName" class="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2 flex items-center gap-2">
							<img src="/icons/v2/user-circle-02-stroke-rounded.svg" alt="" class="w-4 h-4 dark:invert" />
							Nom/Prénom *
						</label>
						<input
							id="fullName"
							type="text"
							bind:value={formData.fullName}
							placeholder="Jean Dupont"
							class="w-full px-3 py-3 text-base border rounded-lg bg-white dark:bg-ord-darkgrey text-gray-900 dark:text-white placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:ring-2 focus:ring-royal-blue/20 focus:border-royal-blue transition-all duration-200 font-medium hover:border-gray-300 dark:hover:border-gray-600 {fieldErrors.fullName ? 'border-red-300 dark:border-red-500' : 'border-gray-200 dark:border-ord-border-dark'}"
						/>
					</div>

					<div in:fly={{ y: 20, duration: 400, delay: 300 }}>
						<label for="email" class="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2 flex items-center gap-2">
							<img src="/icons/v2/mail-01-stroke-rounded.svg" alt="" class="w-4 h-4 dark:invert" />
							Email professionnel *
						</label>
						<input
							id="email"
							type="email"
							bind:value={formData.email}
							placeholder="jean.dupont@entreprise.com"
							class="w-full px-3 py-3 text-base border rounded-lg bg-white dark:bg-ord-darkgrey text-gray-900 dark:text-white placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:ring-2 focus:ring-royal-blue/20 focus:border-royal-blue transition-all duration-200 font-medium hover:border-gray-300 dark:hover:border-gray-600 {fieldErrors.email ? 'border-red-300 dark:border-red-500' : 'border-gray-200 dark:border-ord-border-dark'}"
						/>
					</div>

					<div class="grid grid-cols-2 gap-4" in:fly={{ y: 20, duration: 400, delay: 400 }}>
						<div>
							<label for="company" class="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
								Entreprise *
							</label>
							<input
								id="company"
								type="text"
								bind:value={formData.company}
								placeholder="Entreprise"
								class="w-full px-3 py-3 text-base border rounded-lg bg-white dark:bg-ord-darkgrey text-gray-900 dark:text-white placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:ring-2 focus:ring-royal-blue/20 focus:border-royal-blue transition-all duration-200 font-medium hover:border-gray-300 dark:hover:border-gray-600 {fieldErrors.company ? 'border-red-300 dark:border-red-500' : 'border-gray-200 dark:border-ord-border-dark'}"
							/>
						</div>

						<div>
							<label for="position" class="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
								Poste *
							</label>
							<input
								id="position"
								type="text"
								bind:value={formData.position}
								placeholder="Directeur juridique"
								class="w-full px-3 py-3 text-base border rounded-lg bg-white dark:bg-ord-darkgrey text-gray-900 dark:text-white placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:ring-2 focus:ring-royal-blue/20 focus:border-royal-blue transition-all duration-200 font-medium hover:border-gray-300 dark:hover:border-gray-600 {fieldErrors.position ? 'border-red-300 dark:border-red-500' : 'border-gray-200 dark:border-ord-border-dark'}"
							/>
						</div>
					</div>
				</div>
			{:else}
				<div class="space-y-6" in:fly|global={{ x: -20, duration: 300, delay: 100 }} out:fly|global={{ x: 20, duration: 200 }}>
					<!-- Sector Selection -->
					<div in:fly={{ y: 20, duration: 400, delay: 200 }}>
						<label for="sector" class="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
							Secteur d'activité *
						</label>
						<select
							id="sector"
							bind:value={formData.sector}
							class="w-full px-3 py-3 text-base border border-gray-200 dark:border-ord-border-dark rounded-lg bg-white dark:bg-ord-darkgrey text-gray-900 dark:text-white focus:ring-2 focus:ring-royal-blue/20 focus:border-royal-blue transition-all duration-200 font-medium hover:border-gray-300 dark:hover:border-gray-600"
						>
							<option value="" class="text-gray-500">Sélectionnez votre secteur</option>
							{#each sectors as sector}
								<option value={sector}>{sector}</option>
							{/each}
						</select>
					</div>

					<!-- Team Size Selection -->
					<div in:fly={{ y: 20, duration: 400, delay: 300 }}>
						<!-- svelte-ignore a11y_label_has_associated_control -->
						<label class="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3 flex items-center gap-2">
							<img src="/icons/v2/user-group-stroke-rounded.svg" alt="" class="w-4 h-4 dark:invert" />
							Taille équipe juridique *
						</label>
						<div class="grid grid-cols-4 gap-2">
							{#each teamSizes as size, i}
								<button
									in:scale={{ start: 0.9, duration: 300, delay: 400 + i * 50 }}
									onclick={() => formData.teamSize = size.id}
									class="p-3 rounded-lg border transition-all duration-200 text-center hover:scale-105 active:scale-95 {formData.teamSize === size.id ? 'border-royal-blue bg-royal-blue/5 text-royal-blue scale-105' : 'border-gray-200 dark:border-ord-border-dark hover:border-gray-300 dark:hover:border-gray-600'}"
								>
									<div class="text-lg font-semibold mb-1">{size.label}</div>
									<div class="text-xs text-gray-500 dark:text-gray-400 font-medium">{size.description}</div>
								</button>
							{/each}
						</div>
					</div>

					<!-- Timeline Selection -->
					<div in:fly={{ y: 20, duration: 400, delay: 500 }}>
						<!-- svelte-ignore a11y_label_has_associated_control -->
						<label class="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3 flex items-center gap-2">
							<img src="/icons/v2/calendar-03-stroke-rounded.svg" alt="" class="w-4 h-4 dark:invert" />
							Calendrier *
						</label>
						<div class="grid grid-cols-3 gap-3">
							{#each timelines as timeline, i}
								<button
									in:scale={{ start: 0.9, duration: 300, delay: 600 + i * 50 }}
									onclick={() => formData.timeline = timeline.id}
									class="p-4 rounded-lg border transition-all duration-200 text-center hover:scale-105 active:scale-95 {formData.timeline === timeline.id ? 'border-royal-blue bg-royal-blue/5 text-royal-blue scale-105' : 'border-gray-200 dark:border-ord-border-dark hover:border-gray-300 dark:hover:border-gray-600'}"
								>
									<div class="text-2xl mb-2 transition-transform duration-200 hover:scale-110">{timeline.icon}</div>
									<div class="text-base font-semibold mb-1">{timeline.label}</div>
									<div class="text-xs text-gray-500 dark:text-gray-400 font-medium">{timeline.description}</div>
								</button>
							{/each}
						</div>
					</div>

					<!-- RGPD Consent -->
					<div class="bg-gray-50 dark:bg-gray-800/30 rounded-lg p-4" in:fly={{ y: 20, duration: 400, delay: 700 }}>
						<label class="flex items-start space-x-3 cursor-pointer">
							<input
								id="rgpdConsent"
								type="checkbox"
								bind:checked={formData.rgpdConsent}
								class="mt-0.5 h-5 w-5 flex-none text-royal-blue border-gray-300 rounded transition-all duration-200 hover:scale-110"
							/>
							<div class="flex items-start gap-2">
								<img src="/icons/v2/security-lock-stroke-rounded.svg" alt="" class="w-4 h-4 mt-0.5 dark:invert flex-none opacity-60" />
								<div class="text-sm text-gray-600 dark:text-gray-300 leading-relaxed font-medium">
									J'accepte que mes données personnelles soient traitées conformément à la 
									<a href="/legal/privacy" target="_blank" class="text-royal-blue hover:underline font-semibold transition-all duration-200 hover:text-royal-blue/80">politique de confidentialité</a> 
									pour être recontacté par l'équipe Ordalie. *
								</div>
							</div>
						</label>
					</div>
				</div>
			{/if}
		</div>

		<!-- Footer -->
		<div class="px-6 py-4 border-t border-gray-100 dark:border-ord-border-dark flex justify-between items-center">
			{#if currentPage === 'context'}
				<button
					in:fly={{ x: -20, duration: 300, delay: 200 }}
					onclick={previousPage}
					class="px-4 py-2.5 text-sm font-semibold text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-all duration-200 flex items-center gap-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 hover:scale-105 active:scale-95"
				>
					<img src="/icons/v2/arrow-left-02-stroke-rounded.svg" alt="" class="w-4 h-4 dark:invert transition-transform duration-200 group-hover:-translate-x-1" />
					Retour
				</button>
			{:else}
				<div></div>
			{/if}

			<button
				in:fly={{ x: 20, duration: 300, delay: 300 }}
				onclick={currentPage === 'contact' ? nextPage : submitForm}
				disabled={!isFormValid || isSubmitting}
				class="px-7 py-3 text-base font-semibold rounded-xl transition-all duration-200 flex items-center gap-2 {isFormValid && !isSubmitting
					? 'bg-royal-blue hover:bg-royal-blue/90 hover:shadow-lg hover:scale-105 active:scale-95 text-white shadow-sm' 
					: 'bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400 cursor-not-allowed'}"
			>
				{#if isSubmitting && currentPage === 'context'}
					<svg class="animate-spin h-4 w-4 text-current" fill="none" viewBox="0 0 24 24">
						<circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
						<path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
					</svg>
					Envoi...
				{:else}
					{currentPage === 'contact' ? 'Suivant' : 'Envoyer'}
					{#if currentPage === 'contact'}
						<img src="/icons/v2/arrow-right-02-stroke-rounded.svg" alt="" class="w-4 h-4 invert transition-transform duration-200 group-hover:translate-x-1" />
					{/if}
				{/if}
			</button>
		</div>
	</div>
</div>

<style>
	.modal {
		position: fixed;
		top: 0;
		bottom: 0;
		right: 0;
		left: 0;
		display: flex;
		justify-content: center;
		align-items: center;
		pointer-events: none;
	}
</style>
